import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import { notificationsService } from "../../services/notifications.service";
import { Notification } from "../../types/notification";
import { Permission } from "../../permissions/permission.enum";
import PermissionRoute from "../../components/PermissionRoute";
import DataTable, {
  Column,
  DataTableFetchParams,
} from "../../components/ui/DataTable";

const SentNotifications = () => {
  const { t } = useTranslation("common");

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const res = await notificationsService.listSent({
      page: params.page,
      take: params.pageSize,
    });
    return {
      data: res.data,
      total: res.meta.itemCount,
      totalPages: res.meta.pageCount,
    };
  }, []);

  const columns: Column<Notification>[] = [
    {
      key: "title",
      header: t("title"),
      className: "font-medium text-gray-900 dark:text-gray-100",
    },
    {
      key: "message",
      header: t("message"),
      className: "text-gray-500 dark:text-gray-400 max-w-xs truncate",
    },
    {
      key: "recipient",
      header: t("recipient"),
      render: (n) =>
        n.user ? (
          <div>
            <div className="font-medium">
              {n.user.fullName}
            </div>
            <div className="text-xs">{n.user.email}</div>
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "sent_at",
      header: t("sent_at"),
      render: (n) => new Date(n.createdAt).toLocaleString(),
    },
    {
      key: "status",
      header: t("status"),
      render: (n) =>
        n.isRead ? (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {t("read")}
          </span>
        ) : (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            {t("unread")}
          </span>
        ),
    },
  ];

  return (
    <PermissionRoute permission={Permission.ADMIN_NOTIFICATIONS_READ}>
      <Layout title={t("notifications_sent")}>
        <div className="space-y-6 h-full flex flex-col">
          <Card className="p-4 shadow-sm flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("notifications_sent")}
              </h1>
            </div>
          </Card>
          <DataTable
            className="flex-1 min-h-0"
            columns={columns}
            onFetch={handleFetch}
            initialState={{
              sortBy: "createdAt",
              sortOrder: "DESC",
              pageSize: 50,
            }}
          />
        </div>
      </Layout>
    </PermissionRoute>
  );
};

export default SentNotifications;
